import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Grid, Paper, Alert } from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow/machineHelpers';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'details', title: 'Basic Details' },
  { id: 'classification', title: 'Classification' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  name: string;
  description: string;
  category: string;
  status: string;
}

const INITIAL_VALUES: FormValues = {
  name: '',
  description: '',
  category: '',
  status: 'Active',
};

const CATEGORIES = ['Tutoring', 'Assessment', 'Consulting', 'Other'];

const stepDescriptions: Record<string, string> = {
  details: "Define the tutoring service: subject area, target audience, pricing, and delivery method.",
  classification: "Set the service category and status.",
  review: "Review the service details before creating.",
};

export const ServiceWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const entityResult = await dataProvider.create('services', {
      data: {
        name: v.name,
        description: v.description,
        category: v.category,
        status: v.status,
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('service', 'draft', ['ACTIVATE']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'service',
        entityId,
        entityName: v.name,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;
  const { evaluate } = useRuleEvaluation({ ruleId: 'service:create', context: vals as unknown as Record<string, unknown> });

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField label="Service Name" value={vals.name} onChange={e => updateField('name', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Description" value={vals.description} onChange={e => updateField('description', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'classification':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={vals.category} label="Category" onChange={e => updateField('category', e.target.value)}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={vals.status} label="Status" onChange={e => updateField('status', e.target.value)}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review service details before creating.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Service Info</Typography>
                <Typography variant="body2"><strong>Name:</strong> {vals.name}</Typography>
                <Typography variant="body2"><strong>Description:</strong> {vals.description}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Classification</Typography>
                <Typography variant="body2"><strong>Category:</strong> {vals.category}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {vals.status}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Service Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Name', value: vals.name },
            { label: 'Category', value: vals.category },
            { label: 'Status', value: vals.status },
          ]}
          footerActions={[
            { label: 'View Services', to: '/services' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
